import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Building2, ClipboardList, FileText, Landmark, LayoutDashboard, LogOut, Menu, Package, Receipt, RotateCcw, Settings, ShoppingCart, Truck, Users, Wallet, BarChart3, Boxes, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { group: "Overview", items: [["/", "Dashboard", LayoutDashboard]] },
  { group: "Sales & Billing", items: [["/invoices", "Sales Invoices", FileText], ["/sales-returns", "Sales Returns", RotateCcw], ["/customers", "Customers", Users]] },
  { group: "Purchases", items: [["/bills", "Purchase Bills", ShoppingCart], ["/purchase-returns", "Purchase Returns", RotateCcw], ["/suppliers", "Suppliers", Truck]] },
  { group: "Inventory", items: [["/products", "Products & Services", Package], ["/inventory", "Stock & Ledger", Boxes]] },
  { group: "Financials", items: [["/payments", "Payments", Wallet], ["/expenses", "Expenses", Receipt]] },
  { group: "Reports", items: [["/reports", "Reports", BarChart3]] },
  { group: "System", items: [["/settings", "Settings", Settings], ["/audit", "Audit Log", ClipboardList]] },
];

function Sidebar({ onNavigate }) {
  const { session, can } = useAuth();
  const hidden = (path) => (path === "/audit" && !can("audit:view")) || (path === "/settings" && !can("settings:manage"));
  return (
    <aside className="flex h-full w-64 flex-col bg-[#0F172A] text-slate-100">
      <div className="flex items-center gap-2 px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-600"><Landmark className="h-5 w-5" /></div>
        <div><div className="text-base font-bold leading-tight">BharatLedger</div><div className="text-[11px] text-slate-400">GST-ready business OS</div></div>
      </div>
      <div className="mx-4 mb-2 rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2" data-testid="sidebar-org-name">
        <div className="flex items-center gap-2 text-xs text-slate-400"><Building2 className="h-3.5 w-3.5" /> Organization</div>
        <div className="truncate text-sm font-semibold">{session?.org?.name}</div>
      </div>
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4">
        {NAV.map((g) => (
          <div key={g.group}>
            <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{g.group}</div>
            {g.items.filter(([p]) => !hidden(p)).map(([path, label, Icon]) => (
              <NavLink key={path} to={path} end={path === "/"} onClick={onNavigate} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                <Icon className="h-4 w-4" /> {label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}

export default function AppShell() {
  const { session, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block lg:sticky lg:top-0 lg:h-screen"><Sidebar /></div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full"><Sidebar onNavigate={() => setOpen(false)} /></div>
          <button className="absolute right-4 top-4 rounded-md bg-white p-2" onClick={() => setOpen(false)} data-testid="close-mobile-nav"><X className="h-5 w-5" /></button>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b bg-white/80 px-4 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-mobile-nav"><Menu className="h-5 w-5" /></Button>
            <span className="hidden text-xs text-slate-500 sm:inline">Signed in as</span>
            <span className="text-sm font-semibold" data-testid="header-user-name">{session?.user?.name}</span>
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-blue-700" data-testid="header-role-badge">{session?.role}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button"><LogOut className="mr-2 h-4 w-4" /> Sign out</Button>
        </header>
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8"><Outlet /></main>
      </div>
    </div>
  );
}
